import { Button } from "@/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { Input } from "@/components/input";
import { ACTIONS_CONFIG } from "@/pages/Projects/Editor/Actions/shared";
import { PlusIcon } from "@radix-ui/react-icons";
import { useState } from "react";

import { useCreateNode, useStructuredWorkflow } from "@/api/workflow-hooks";
import {
  type ActionNode,
  type ActionSubtype,
  createDefaultExportAction,
  createDefaultRESTAction,
  createDefaultRPAAction,
  createDefaultSendEmailAction,
} from "@repo/shared/workflow";

interface FormValues {
  name: string;
  subtype: ActionSubtype;
}

interface CreateActionDialogProps {
  workflowId: string;
  actionGroupNodeId: string;
  open: boolean;
  onClose: () => void;
  onOpenChange: (open: boolean) => void;
}

export const CreateActionDialog = ({
  workflowId,
  actionGroupNodeId,
  open,
  onClose,
  onOpenChange,
}: CreateActionDialogProps) => {
  const [values, setValues] = useState<FormValues>({
    name: "",
    subtype: "rest",
  });
  const createNodeMut = useCreateNode(workflowId);
  const { triggerGroupNodeId } = useStructuredWorkflow(workflowId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name) return;

    let newAction: ActionNode;
    if (values.subtype === "export") {
      newAction = createDefaultExportAction();
    } else if (values.subtype === "rpa") {
      newAction = createDefaultRPAAction();
    } else if (values.subtype === "send-email") {
      if (!triggerGroupNodeId) {
        console.error("Could not find trigger group");
        return;
      }
      newAction = createDefaultSendEmailAction();
      newAction.dependencyIds = [triggerGroupNodeId];
    } else {
      newAction = createDefaultRESTAction();
    }

    newAction.name = values.name;

    await createNodeMut.mutateAsync({
      groupId: actionGroupNodeId,
      data: newAction,
    });

    onClose();
    setValues({ name: "", subtype: "rest" });
  };

  return (
    <>
      <Button
        onClick={() => onOpenChange(true)}
        variant="ghost"
        className="ml-2 pl-2.5"
      >
        <PlusIcon className="mr-2" />
        Action
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Action</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block mb-2">
                Name
              </label>
              <Input
                id="name"
                value={values.name}
                onChange={(e) => setValues({ ...values, name: e.target.value })}
                required
                placeholder="Enter name..."
              />
            </div>

            <div>
              <label htmlFor="type" className="block mb-2">
                Type
              </label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(ACTIONS_CONFIG).map(
                  ([subtype, { icon, label, hide }]) => {
                    if (hide) return null;

                    return (
                      <button
                        key={subtype}
                        type="button"
                        onClick={() =>
                          setValues({
                            ...values,
                            subtype: subtype as ActionSubtype,
                          })
                        }
                        className={`p-4 border rounded-lg flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors ${
                          values.subtype === subtype
                            ? "border-primary ring-2 ring-accent"
                            : "border-gray-200"
                        }`}
                      >
                        <img src={icon} alt={label} className="w-12 h-12" />
                        <span className="font-medium">{label}</span>
                      </button>
                    );
                  },
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="">
                Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
